import { Form, Formik, useFormikContext } from "formik";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useMemo, useEffect } from "react";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Input } from "../../components/Input";
import { Paragraph } from "../../components/Paragraph";
import { TextArea } from "../../components/TextArea";
import {
  CreateMandateTypePayload,
  useCreateMandateType,
} from "../../data/useCreateMandateType";
import { notify } from "../../utils/notify";
import { settingsCreateApplicationTypeValidation } from "../../utils/validationSchema";
import { Image } from "../../components/Image";
import { Toggle } from "../../components/Toggle";
import Email from "../../assets/Mail-Email-Letter.svg";
import { ApplicationTypeNewFrame } from "./ApplicationTypeNewFrame";
import { ApplicationTypeDivisionFields } from "./ApplicationTypeDivisionFields";
import { Icon } from "../../components/Icon";
import { Tooltip } from "../../components/Tooltip";
import Select from "../../components/Select";
import { useBillingInfo } from "../../data/useBillingInfo";

const TYPE_OPTIONS = [
  { label: "Individual", value: "individual" },
  { label: "Entity", value: "entity" },
];

type FormValues = {
  name: string;
  description: string;
  action_reminder_email: boolean;
  _type: "individual" | "entity" | "";
  divisions: number[];
};

const EnforceIndividualType = ({
  profilesEnabled,
}: {
  profilesEnabled: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    if (!profilesEnabled && values._type !== "individual") {
      setFieldValue("_type", "individual");
    }
  }, [profilesEnabled, values._type, setFieldValue]);

  return null;
};

const SettingsApplicationTypeNew = () => {
  const { mutateAsync: createMandateType } = useCreateMandateType();
  const navigate = useNavigate();

  const { data: billingData } = useBillingInfo();
  const billingInfo =
    billingData?.data?.results?.length > 0 ? billingData.data.results[0] : null;

  const profilesEnabled = Boolean(billingInfo?.profiles_enabled);

  const typeOptions = useMemo(
    () =>
      profilesEnabled
        ? TYPE_OPTIONS
        : TYPE_OPTIONS.filter((o) => o.value === "individual"),
    [profilesEnabled],
  );

  const handleSubmit = (data: FormValues) => {
    const safeType: "individual" | "entity" =
      profilesEnabled && data._type === "entity" ? "entity" : "individual";

    const payload: CreateMandateTypePayload = {
      ...data,
      _type: safeType,
      checkpoint: [],
      is_disabled: true,
    } as CreateMandateTypePayload;

    // Return the promise so Formik keeps isSubmitting true until the request
    // settles; otherwise the button re-enables at once and a second click
    // creates a duplicate type.
    return createMandateType(payload)
      .then((res: any) => {
        if (res?.status?.toString().startsWith("2")) {
          notify("Application Type created successfully!", {
            type: "success",
          });
        }
        navigate(`/settings/application-types/${res.data.id}/screening`);
      })
      // Errors are surfaced by the global axios interceptor.
      .catch(() => undefined);
  };

  return (
    <ApplicationTypeNewFrame step="details">
      <Formik<FormValues>
        initialValues={{
          name: "",
          description: "",
          action_reminder_email: false,
          _type: profilesEnabled ? "" : "individual",
          divisions: [],
        }}
        onSubmit={handleSubmit}
        validate={() => null}
        validationSchema={settingsCreateApplicationTypeValidation}
      >
        {(props) => (
          <Form onSubmit={props.handleSubmit}>
            <EnforceIndividualType profilesEnabled={profilesEnabled} />

            <Flex
              bg="white"
              width="100%"
              gap="12"
              justifyContent="space-between"
              flexWrap="wrap"
              borderRadius="16px"
              pb={4}
            >
              <Box pl={4} pr={2} pt={4} flex={4} minWidth={400}>
                <H2 fontSize="3">Create New Application Type</H2>
                <Paragraph fontWeight={500}>Application Details</Paragraph>

                <Box>
                  {profilesEnabled && (
                    <Box my={3}>
                      <Select
                        label="Type"
                        isRequired
                        hasError={Boolean(props.errors._type)}
                        errorMessage={props.errors._type as string}
                        options={typeOptions as any}
                        value={
                          typeOptions.find(
                            (o) => o.value === props.values._type,
                          ) as any
                        }
                        onChange={(opt: any) =>
                          props.setFieldValue(
                            "_type",
                            (opt?.value ?? "individual") as
                              | "individual"
                              | "entity",
                          )
                        }
                        width="auto"
                        isDisabled={props.isSubmitting}
                      />
                    </Box>
                  )}

                  <ApplicationTypeDivisionFields isDisabled={props.isSubmitting} />

                  <Box my={3}>
                    <Input
                      onChange={props.handleChange}
                      value={props.values.name}
                      placeholder="Enter an application type name"
                      label="Application Type Name"
                      name="name"
                      isRequired
                      hasError={Boolean(props.errors.name)}
                      errorMessage={props.errors.name as any}
                    />
                  </Box>

                  <Box my={3}>
                    <TextArea
                      onChange={props.handleChange}
                      value={props.values.description}
                      placeholder="Enter a description"
                      label="Description"
                      name="description"
                      rows={4}
                      isRequired
                      hasError={Boolean(props.errors.description)}
                      errorMessage={props.errors.description as any}
                    />
                  </Box>

                  <Flex
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Flex gap="12">
                      <Image
                        src={Email}
                        alt="actions icon"
                        height="20px"
                        width="24px"
                      />
                      <Flex alignItems="center" gap="8">
                        <Paragraph m="0" fontWeight={600}>
                          Auto-email reminders
                        </Paragraph>
                        <Tooltip content="The customer will be emailed a reminder to complete the process each day before their invite expires.">
                          <Icon
                            Type={IoMdInformationCircleOutline}
                            aria-hidden="true"
                            height={15}
                          />
                        </Tooltip>
                      </Flex>
                    </Flex>

                    <Toggle
                      mt={1}
                      isChecked={props.values.action_reminder_email}
                      onChange={(e: any) =>
                        props.setFieldValue(
                          "action_reminder_email",
                          e.target.checked,
                        )
                      }
                    />
                  </Flex>

                  <Box mt={4}>
                    <Flex justifyContent="end">
                      <Button
                        size="large"
                        type="submit"
                        isDisabled={
                          props.isSubmitting ||
                          !props.dirty ||
                          (props.dirty && !props.isValid)
                        }
                      >
                        Continue
                      </Button>
                    </Flex>
                  </Box>
                </Box>
              </Box>
            </Flex>
          </Form>
        )}
      </Formik>
    </ApplicationTypeNewFrame>
  );
};

export default SettingsApplicationTypeNew;
